import { getSubdomain } from './../store/ui/ui.selectors';
import {
  RefreshToken,
  RefreshTokenSuccess,
  AuthActionTypes,
} from './../../auth/store/auth.actions';
import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import {
  map,
  catchError,
  filter,
  take,
  switchMap,
  withLatestFrom,
  tap,
} from 'rxjs/operators';

import { IStore } from '../root.store';
import { environment } from './../../../environments/environment';
import { getLocaleOffsetDate } from '../helpers/date.helper';
import { Actions, ofType } from '@ngrx/effects';
import { selectDefaultAuthEntity } from 'src/app/auth/store/auth.selectors';

// https://github.com/angular/angular/blob/master/packages/common/http/src/xhr.ts#L18
const XSSI_PREFIX = /^\)\]\}',?\n/;

@Injectable()
export class AppHttpInterceptorService implements HttpInterceptor {
  constructor(private store$: Store<IStore>, private actions$: Actions) { }

  private isRefreshing = false;
  private dbName = '';
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  /**
   * Custom http request interceptor
   * @public
   * @param {HttpRequest<any>} req
   * @param {HttpHandler} next
   * @returns {Observable<HttpEvent<any>>}
   * @memberof JsonInterceptor
   */
  public intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    const authToken = localStorage.getItem(environment.auth.token);

    this.store$
      .select(getSubdomain)
      .pipe(
        tap(dbName => {
          this.dbName = dbName;
          req = this.addToken(req, authToken);
        }),
        take(1)
      )
      .subscribe();


    return next.handle(req).pipe(
      map((event) => {
        // Pass through everything except for the final response.
        if (!(event instanceof HttpResponse)) {
          return event;
        }
        return this.processJsonResponse(event);
      }),
      catchError((error) => {
        if (authToken && error instanceof HttpErrorResponse && error.status === 401 && req.url.indexOf('reset-password') < 0 ) {
          return this.handle401Error(req, next);
        } else {
          return (this.handleError(error));
        }
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    // convert to responseType of text to skip angular parsing
    request = request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      setParams: { database: this.dbName }
    });

    return request;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      this.store$.dispatch(new RefreshToken());
      // we have to wait for the asynchronous call to finish. So we will listen to this event.
      return this.actions$.pipe(
        ofType<RefreshTokenSuccess>(AuthActionTypes.REFRESH_TOKEN_SUCCESS),
        withLatestFrom(
          this.store$.select(
            selectDefaultAuthEntity('5334f7c7-16e6-4714-9fce-8a2a6e60e79c')
          )
        ),
        switchMap((result) => {
          this.isRefreshing = false;
          if (result) {
            const authCommon = result[1];
            this.refreshTokenSubject.next(authCommon.accessToken);
            return next.handle(this.addToken(request, authCommon.accessToken));
          } else {
            this.refreshTokenSubject.next(null);
            return next.handle(null);
          }
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        })
      );
    }
  }

  /**
   * Parse the json body using custom revivers.
   * @private
   * @param {HttpResponse<string>} res
   * @returns{HttpResponse<any>}
   * @memberof JsonInterceptor
   */
  private processJsonResponse(res: HttpResponse<string>): HttpResponse<any> {
    let body = res.body;
    if (typeof body === 'string') {
      const originalBody = body;
      body = body.replace(XSSI_PREFIX, '');
      try {
        body =
          body !== ''
            ? JSON.parse(body, (key: any, value: any) =>
              this.reviveUtcDate(key, value)
            )
            : null;
      } catch (error) {
        if (res.ok) { // blob could not be parsed as json
          return res.clone({ body });
        }
        // match https://github.com/angular/angular/blob/master/packages/common/http/src/xhr.ts#L221
        throw new HttpErrorResponse({
          error: { error, text: originalBody },
          headers: res.headers,
          status: res.status,
          statusText: res.statusText,
          url: res.url || undefined,
        });
      }
    }
    return res.clone({ body });
  }

  /**
   * Detect a date string and convert it to a date object.
   * @private
   * @param {*} key json property key.
   * @param {*} value json property value.
   * @returns {*} original value or the parsed date.
   * @memberof JsonInterceptor
   */
  private reviveUtcDate(key: any, value: any): any {
    if (typeof value !== 'string') {
      return value;
    }
    if (value === '0001-01-01T00:00:00' || value === '1901-01-01T00:00:00') {
      return null;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)Z?$/.exec(
      value
    );
    if (!match) {
      return value;
    }
    // getCurrentUtcDate();
    // getCurrentLocaleDate();
    // getUtcDate(value);
    // getAuDate(value);
    return getLocaleOffsetDate(value);
  }

  private handleError(err) {
    console.error(err);
    return throwError(err);
  }


}
